import { useState } from "react";
import { useForm } from "react-hook-form";
import { EstadoPreventaEnum, EstadoProspectoEnum, EstadosAgendamientoEnumChoice, EstadoFactibilidadCorporativaENUM, ModulosEnumChoice, newRechazoGeneralSchema, ROUTER_PATHS } from "@/shared";
import { useFetchMotivosRechazo, useFetchUpdateProspecto, useUpdateAgendamiento, useUpdatePreagendamiento, useUpdatePreventa } from "@/shared/actions/erp";
import { Agendamiento, EstadoBitacoraEnum, IPreagendamiento } from "@/shared/interfaces";
import { useDiccionarioMotivo } from "@/store/erp/diccionario_motivo/diccionario_motivo.store";
import { useFetchPutFactibilidadCorporativo } from "@/shared/actions/erp/factibilidad_corporativo/factibilidad_corporativo.actions";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { usePutProspectoPymes } from "@/shared/actions/erp/prospecto_pymes.action";
import { useFetchPutPreventaPymes } from "@/shared/actions/erp/preventa_pymes/preventa_pymes.action";
import { useFetchPutPreagendamientoPymes } from "@/shared/actions/erp/operaciones/preagendamiento/preagendamiento_pymes.actions";

export interface FormMotivoRechazo {
  id: string
  motivo_rechazo: string
  observacion_cancelacion: string
  razon_social?: string
  identificacion?: string
  observaciones?: any | any[]
  id_preagendamiento?: string ///* para cancelar bitacora
  productos_costeados?: any
  detalles?: any
}

interface UseModalMotivoRechazo {
  modulo: ModulosEnumChoice,
  option?: string,
  returnUrlFactibilidadCorp?: string
  gestionVenta?: boolean
  returnUrl?: string

}
export function useModalMotivoRechazo({
  modulo = ModulosEnumChoice.PROSPECTO,
  option,
  returnUrlFactibilidadCorp = ROUTER_PATHS.ventasCorporativas.solicitudesPendientesCorporativoNav,
  gestionVenta = false,
  returnUrl
}: UseModalMotivoRechazo) {
  const [modalCancelar, setModalCancelar] = useState(false);
  const [dialogContentText, setDialogContentText] = useState<string | null>(null)
  const [labelMotivo, setLabelMotivo] = useState<string>("Motivo de rechazo")

  const navigate = useNavigate()

  ///* formulario
  const formRechazo = useForm<FormMotivoRechazo, any, FormMotivoRechazo>({
    resolver: yupResolver(newRechazoGeneralSchema)
  });

  const { reset } = formRechazo;

  ///* store

  const emitFinalizarVenta = useDiccionarioMotivo(s => s.emitFinalizarVenta)

  ///* react query
  const { mutateAsync: mutateAsyncProspecto } = useFetchUpdateProspecto({
    enableAlert: true,
  });

  const { mutateAsync: mutateAsyncProspectoPymes } = usePutProspectoPymes({
    enableAlert: true,
  });

  const { mutateAsync: updatePreventa } = useUpdatePreventa({
    enableAlert: true
  });

  const { mutateAsync: updatePreventaPymes } = useFetchPutPreventaPymes({
    enableAlert: true
  })

  const {
    mutateAsync: mutateAsyncPreagendamiento
  } = useUpdatePreagendamiento<Partial<IPreagendamiento>>({})

  const {
    mutateAsync: mutateAsyncPreagendamientoPymes
  } = useFetchPutPreagendamientoPymes({})

  const {
    mutateAsync: mutateAsyncAgendamiento
  } = useUpdateAgendamiento<Partial<Agendamiento>>()

  const {
    mutateAsync: mutateAsyncFactibilidadCorporativa
  } = useFetchPutFactibilidadCorporativo({
    navigate: navigate,
    returnUrl: returnUrlFactibilidadCorp
  })


  const {
    data: motivosRechazoPaging,
    isLoading: isLoadingMotivosRechazo,
    isRefetching: isRefetchingMotivosRechazo,
  } = useFetchMotivosRechazo({
    estado: true,
    modulo,
    page_size: 100,
  });

  ///* handle
  const onConfirm = async (_form: FormMotivoRechazo) => {


    try {

      let resp: {
        code: number
      } = {
        code: 500
      }

      const values = formRechazo.getValues();

      switch (modulo) {
        case ModulosEnumChoice.PROSPECTO:
          resp = await mutateAsyncProspecto({
            id: values.id,
            data: {
              estado_prospecto: EstadoProspectoEnum.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                observacion_cancelar: values.observacion_cancelacion ?? 'N/A'
              },
            },
          });
          break;

        case ModulosEnumChoice.PREVENTA:

          resp = await updatePreventa({
            id: values.id,
            data: {
              estado_preventa: gestionVenta ? EstadoPreventaEnum.PRE_RECHAZADO : EstadoPreventaEnum.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                ...values.observaciones,
                observacion_cancelar: values.observacion_cancelacion ?? 'N/A'
              }
            }
          });

          break;

        case ModulosEnumChoice.PREAGENDAMIENTO:

          resp = await mutateAsyncPreagendamiento({
            id: values.id,
            data: {
              estado_preagendamiento: EstadoPreventaEnum.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                ...values.observaciones,
                observacion_cancelacion: values.observacion_cancelacion ?? 'N/A'
              }
            }
          });

          if (resp.code == 200)
            emitFinalizarVenta({ numero_referencia_venta: values.id, estado_venta: EstadoBitacoraEnum.CANCELADO })


          break;

        case ModulosEnumChoice.AGENDAMIENTO:

          resp = await mutateAsyncAgendamiento({
            id: values.id,
            data: {
              estado_agendamiento: EstadosAgendamientoEnumChoice.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                ...values.observaciones,
                observacion_cancelacion: values.observacion_cancelacion ?? 'N/A'
              }
            }
          });

          if (resp.code === 200)
            emitFinalizarVenta({ numero_referencia_venta: values.id_preagendamiento!, estado_venta: EstadoBitacoraEnum.CANCELADO })


          break;

        case ModulosEnumChoice.FACTIBILIDAD_CORPORATIVO:
          if (option && option === EstadoFactibilidadCorporativaENUM.CANCELADO) {
            resp = await mutateAsyncFactibilidadCorporativa({
              id_factibilidad_coorporativa: values.id,
              estado_factibilidad: EstadoFactibilidadCorporativaENUM.CANCELADO,
              observacion_rechazo: [
                {
                  motivo: values.observacion_cancelacion,
                  observacion: values.motivo_rechazo,
                  fecha: (new Date()).toISOString()
                }
              ]
            })
            return
          }

          resp = await mutateAsyncFactibilidadCorporativa({
            id_factibilidad_coorporativa: values.id,
            estado_factibilidad: EstadoFactibilidadCorporativaENUM.EN_REVISION,
            productos_costeados: values.productos_costeados,
            detalles:values.detalles,
            observaciones: {
              observacion_revision_supervisor: [
                ...values?.observaciones?.observacion_revision_supervisor || "",
                {
                  motivo_rechazo: values?.motivo_rechazo || "N/A",
                  observacion_cancelacion: values?.observacion_cancelacion || "N/A",
                  fecha: new Date(),
                }
              ]
            }
          });

          break;

        case ModulosEnumChoice.PROSPECTO_PYMES:

          resp = await mutateAsyncProspectoPymes({
            id: values.id,
            data: {
              estado_prospecto: EstadoProspectoEnum.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                observacion_cancelar: values.observacion_cancelacion ?? 'N/A'
              },
            },
          });

          break;

        case ModulosEnumChoice.PREVENTA_PYMES:

          resp = await updatePreventaPymes({
            id: values.id,
            data: {
              estado_preventa: gestionVenta ? EstadoPreventaEnum.PRE_RECHAZADO : EstadoPreventaEnum.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                ...values.observaciones,
                observacion_cancelar: values.observacion_cancelacion ?? 'N/A'
              }
            }
          });

          break;

        case ModulosEnumChoice.PREAGENDAMIENTO_PYMES:

          resp = await mutateAsyncPreagendamientoPymes({
            id: values.id,
            data: {
              estado_preagendamiento: EstadoPreventaEnum.CANCELADO,
              motivo_rechazo: values.motivo_rechazo,
              observaciones: {
                ...values.observaciones,
                observacion_cancelar: values.observacion_cancelacion ?? 'N/A'
              }
            }
          });

          break;

        default:
          break;
      }

      if (resp.code === 200) {
        onClose()
      }

    } catch (error) {
      console.log('error hook rechazar:', error);

    }
  };

  const onClose = () => {
    reset({});
    setModalCancelar(false);
    returnUrl && navigate(returnUrl);
  };


  return {
    modalCancelar,
    setModalCancelar,
    formRechazo,
    motivosRechazoPaging: motivosRechazoPaging?.data,
    isLoadingMotivosRechazo,
    isRefetchingMotivosRechazo,
    onConfirm,
    onClose,
    reset,
    dialogContentText,
    setDialogContentText,
    labelMotivo,
    setLabelMotivo,

  };
}
